import styled from '@emotion/styled';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import P1 from '@base/P1/P1';
import { getDay, getMonthString, getYear } from '@utils/dates';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import EarlyMorning from '@icon/EarlyMorning';
import Morning from '@icon/Morning';
import Afternoon from '@icon/Afternoon';
import Evening from '@icon/Evening';
import LateEvening from '@icon/LateEvening';
import Clock from '@icon/Clock';
import CalendarSleek from '@icon/CalendarSleek';
import EducatorAvatar from '@icon/EducatorAvatar';
import { getUserById } from '@selectors/learner';
import { emptyList } from '@constants/empty';

const classTimeIconMap = {
  1: EarlyMorning,
  2: Morning,
  3: Afternoon,
  4: Evening,
  5: LateEvening,
  6: Clock,
  7: Clock,
  20: Clock
};

const InfoRow = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_3B};
  :last-child {
    margin-bottom: 0;
  }
`;

const IconWrap = styled.div`
  margin-right: ${Spacings.SPACING_2B};
  width: 20px;
  height: 20px;
`;

const StyledP1 = styled(P1)`
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.P2};
    line-height: 20px;
  }
`;

const oneDay = 24 * 60 * 60 * 1000;

const getDateInfo = (startAt, endAt) => {
  if (!startAt) return null;
  const start = new Date(startAt).setHours(0, 0, 0, 0);
  const end = endAt && new Date(endAt).setHours(0, 0, 0, 0);
  const current = new Date().setHours(0, 0, 0, 0);
  const startText = `${getDay(startAt)} ${getMonthString(startAt)} ${getYear(
    startAt
  )}`;
  const endText =
    endAt && `${getDay(endAt)} ${getMonthString(endAt)} ${getYear(endAt)}`;
  let dateInfo = '';
  if (start >= current) {
    const diff = Math.ceil((start - current) / oneDay);
    if (diff < 1) {
      dateInfo = `Starts today · ${startText}`;
    } else if (diff < 2) {
      dateInfo = `Starts tomorrow · ${startText}`;
    } else if (diff > 1 && diff < 8) {
      dateInfo = `Starts in ${diff} days · ${startText}`;
    } else {
      dateInfo = `Starts on ${startText}`;
    }
  } else if (start < current && (!end || current < end)) {
    dateInfo = `Started on ${startText}`;
  } else {
    dateInfo = `Ended on ${endText}`;
  }
  return dateInfo;
};

const BatchCardInfo = ({ batch, authors, firstAuthorUsername }) => {
  const { startsAt, endsAt, timeType } = batch;
  const batchAuthors = authors || batch.authors;
  const dateInfo = getDateInfo(startsAt, endsAt);
  const typeOfClassTime = timeType?.type;
  const ClassTimeType = typeOfClassTime && classTimeIconMap[typeOfClassTime];
  const firstUser = useSelector(getUserById(firstAuthorUsername));

  const authorsInfo = useMemo(() => {
    const firstUserName = firstUser?.name;
    const authorsNameArr =
      batchAuthors?.map((author) => author.name) || emptyList;

    if (firstUserName) {
      if (authorsNameArr.length) {
        const index = authorsNameArr.indexOf(firstUserName);
        authorsNameArr.splice(index, 1);
        authorsNameArr.splice(0, 0, firstUserName);
      } else {
        return firstUserName;
      }
    }

    if (authorsNameArr.length) {
      if (authorsNameArr.length < 2) {
        return authorsNameArr[0];
      }
      if (authorsNameArr.length > 1 && authorsNameArr.length < 4) {
        return `${authorsNameArr
          .slice(0, -1)
          .join(', ')} and ${authorsNameArr.slice(-1)}`;
      }
      return `${authorsNameArr.slice(0, 3).join(', ')} and ${
        authorsNameArr.length - 3
      } more`;
    }
  }, [batchAuthors, firstUser?.name]);

  return (
    <>
      {dateInfo && (
        <InfoRow>
          <IconWrap>
            <CalendarSleek
              size="20px"
              aria-hidden="true"
              color="var(--color-text-primary)"
            />
          </IconWrap>
          <StyledP1 data-testid="dateInfo">{dateInfo}</StyledP1>
        </InfoRow>
      )}
      {typeOfClassTime && (
        <InfoRow>
          <IconWrap>
            <ClassTimeType
              size="20px"
              aria-hidden="true"
              color="var(--color-text-primary)"
            />
          </IconWrap>
          <StyledP1>{timeType.text}</StyledP1>
        </InfoRow>
      )}
      {authorsInfo && (
        <InfoRow>
          <IconWrap>
            <EducatorAvatar
              size="20px"
              aria-hidden="true"
              color="var(--color-text-primary)"
            />
          </IconWrap>
          <StyledP1 title={authorsInfo} lineClamp={2}>
            {authorsInfo}
          </StyledP1>
        </InfoRow>
      )}
    </>
  );
};

BatchCardInfo.propTypes = {
  batch: PropTypes.objectOf(PropTypes.any).isRequired,
  firstAuthorUsername: PropTypes.string
};

BatchCardInfo.defaultProps = {
  firstAuthorUsername: ''
};

export default BatchCardInfo;
